import React, { useState, useEffect } from 'react';
import { Table, Button, Badge, Spinner, Alert, Accordion, Pagination } from 'react-bootstrap';
import { useParams } from 'react-router-dom';

export default function CompactQuestionsViewer({ onEditQuestion }) {
  const [questions, setQuestions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const {mock_id} = useParams()
  
  // Pagination State for 50+ list scale
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;

  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(`https://coda.easylinksmw.com/api/questions/getQuestions/${mock_id}`);
        if (!response.ok) throw new Error('Failed to fetch questions.');
        const data = await response.json();
        
        if (data.message === "success" && Array.isArray(data.questions)) {
          setQuestions(data.questions);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };
    fetchQuestions();
  }, []);

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
        <Spinner animation="border" variant="primary" className="me-2" />
        <span className="text-dark fw-semibold">Loading dashboard...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mt-4" style={{ maxWidth: '1000px' }}>
        <Alert variant="danger">{error}</Alert>
      </div>
    );
  }

  // Calculate pages
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentQuestions = questions.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(questions.length / itemsPerPage);

  return (
    <div className="container-fluid py-4" style={{ maxWidth: '1200px' }}>
      <div className="d-flex justify-content-between align-items-center mb-4 border-bottom pb-2">
        <div>
          <h3 className="text-dark fw-bold m-0">Question Bank Management</h3>
          <small className="text-muted">Reviewing {questions.length} total entries</small>
        </div>
      </div>

      <div className="bg-white border rounded shadow-sm">
        {/* Accordion Table Header Row */}
        <div className="d-none d-md-flex align-items-center bg-light fw-bold p-3 border-bottom text-dark">
          <div style={{ width: '80px' }}>#</div>
          <div className="flex-grow-1">Question</div>
          <div style={{ width: '150px' }} className="text-center">Correct Key</div>
          <div style={{ width: '100px' }} className="text-end">Actions</div>
        </div>

        {/* Collapsible Accordion Core Container */}
        <Accordion flush>
          {currentQuestions.map((q, idx) => {
            // Find correct character string to show in summary row row field
            const correctAnswer = q.answers?.find(a => a.isCorrect)?.answer_option || 'None';
            const globalIndex = indexOfFirstItem + idx + 1;

            return (
              <Accordion.Item eventKey={q.id} key={q.id} className="border-bottom text-dark">
                
                {/* Header Strip Toggle */}
                <div className="d-flex align-items-center p-3 align-middle bg-white hover-row">
                  <div style={{ width: '80px' }} className="fw-bold text-secondary">
                    {q.question_number || globalIndex}
                  </div>
                  
                  {/* Text truncate prevents layout blowouts for super long test strings */}
                  <div className="flex-grow-1 text-truncate pe-3 fw-medium text-dark">
                    {q.question_desc}
                  </div>
                  
                  <div style={{ width: '150px' }} className="text-center">
                    <Badge bg="success" className="px-2 py-1 fs-6">{correctAnswer}</Badge>
                  </div>
                  
                  <div style={{ width: '100px' }} className="d-flex justify-content-end gap-2">
                    {/* <Button 
                      variant="primary" 
                      size="sm" 
                      onClick={(e) => {
                        e.stopPropagation(); // Blocks triggering accordion drawer slider
                        onEditQuestion(q);
                      }}
                    >
                      Edit
                    </Button> */}
                    <Accordion.Button style={{ width: 'auto', padding: '0 0.5rem', background: 'none', border: 'none' }} />
                  </div>
                </div>

                {/* Collapsible Options Drawer Panel */}
                <Accordion.Body className="bg-light-subtle px-4 py-3 border-top">
                  {/* <div className="mb-2 fw-bold text-secondary text-uppercase small">Full Question Prompt:</div> */}
                  {/* <p className="text-dark fs-6 mb-3 whitespace-pre-wrap">{q.question_desc}</p> */}
                  
                  <div className="fw-bold text-secondary text-uppercase small mb-2">Answer Options:</div>
                  <Table size="sm" bordered responsive className="bg-white m-0">
                    <thead>
                      <tr className="table-light">
                        <th style={{ width: '60px' }} className="text-center">Key</th>
                        <th>Option Description</th>
                        <th style={{ width: '100px' }} className="text-center">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {q.answers?.map((ans) => (
                        <tr key={ans.id} className={ans.isCorrect ? 'table-success-subtle font-weight-bold' : ''}>
                          <td className="text-center fw-bold">{ans.answer_option}</td>
                          <td className="text-dark">{ans.answer_desc}</td>
                          <td className="text-center">
                            {ans.isCorrect ? (
                              <Badge bg="success">Correct</Badge>
                            ) : (
                              <span className="text-muted small">—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Accordion.Body>
              </Accordion.Item>
            );
          })}
        </Accordion>
      </div>

      {/* Pagination Controls Footer block to avoid infinite scrolling */}
      {totalPages > 1 && (
        <div className="d-flex justify-content-center mt-4">
          <Pagination>
            <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
            <Pagination.Prev onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1} />
            
            {[...Array(totalPages).keys()].map(page => (
              <Pagination.Item
                key={page + 1}
                active={page + 1 === currentPage}
                onClick={() => setCurrentPage(page + 1)}
              >
                {page + 1}
              </Pagination.Item>
            ))}

            <Pagination.Next onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages} />
            <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} />
          </Pagination>
        </div>
      )}
    </div>
  );
}
